/**
 * Chat Layout - Vitreous Support
 * CoreUI-themed responsive conversation list + chat area
 */

import React, { useState, useEffect } from 'react';
import { Button, ConfigProvider } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { ChatThemeProvider } from '../../contexts/ChatThemeContext';
import { ConversationList } from './ConversationList';
import { MessageArea } from './MessageArea';
import { ChatSearch } from './ChatSearch';
import { useChatNotifications } from '../../hooks/useChatNotifications';
import type { ThemeConfig } from 'antd';
import '../../theme/coreui-chat-theme.css';
import './ChatLayout.css';

/* CoreUI theme for Ant Design components in chat */
const coreuiChatTheme: ThemeConfig = {
  token: {
    colorPrimary: '#5856d6',
    colorSuccess: '#1b9e3e',
    colorWarning: '#f9b115',
    colorError: '#e55353',
    colorInfo: '#3399ff',
    colorBgContainer: '#ffffff',
    colorBorder: '#dbdfe6',
  },
};

export const ChatLayout: React.FC = () => {
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  useChatNotifications(selectedChannelId);

  /* Hide Jotform widgets that overlap chat input */
  useEffect(() => {
    const hideJotform = () => {
      document.querySelectorAll('[id^="jf-"], [class*="jf-"]').forEach(el => {
        (el as HTMLElement).style.setProperty('display', 'none', 'important');
        (el as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
      });
    };
    hideJotform();
    const t1 = setTimeout(hideJotform, 500);
    const t2 = setTimeout(hideJotform, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape' && showSearch) setShowSearch(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  return (
    <ChatThemeProvider>
      <ConfigProvider theme={coreuiChatTheme}>
        <div className="chat-layout chat-coreui-theme">
        <aside
          className={`chat-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
        >
          <ConversationList
            selectedConversationId={selectedChannelId}
            onSelectConversation={setSelectedChannelId}
          />
        </aside>

        <Button
          type="text"
          icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          className={`chat-sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Show conversations' : 'Hide conversations'}
        />

        <main className="chat-content-area">
          <div className="chat-content">
            <MessageArea
              channelId={selectedChannelId}
              showChannelInfo={false}
              onOpenThread={() => {}}
            />
          </div>
        </main>

        <ChatSearch
          open={showSearch}
          onClose={() => setShowSearch(false)}
          onSelectChannel={(id) => {
            setSelectedChannelId(id);
            setShowSearch(false);
          }}
        />
        </div>
      </ConfigProvider>
    </ChatThemeProvider>
  );
};
